import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import toast from 'react-hot-toast';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: any) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    const message = error.response?.data?.message || error.message || 'An error occurred';
    
    // Handle specific error cases
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      toast.error('Session expired. Please login again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied. Insufficient permissions.');
    } else if (error.response?.status === 404) {
      toast.error('Resource not found.');
    } else if (error.response?.status >= 500) {
      toast.error(message || 'Server error. Please try again later.');
    } else {
      // Show error message from API or generic message
      toast.error(message);
    }
    
    return Promise.reject(error);
  }
);

// API methods
export const apiClient = {
  // Generic methods
  get: <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    api.get(url, config).then(response => response.data),
    
  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    api.post(url, data, config).then(response => response.data),
    
  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    api.put(url, data, config).then(response => response.data),
    
  delete: <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    api.delete(url, config).then(response => response.data),

  // Auth endpoints
  auth: {
    login: (email: string, password: string) =>
      apiClient.post('/auth/login', { email, password }),
      
    register: (userData: any) =>
      apiClient.post('/auth/register', userData),
      
    getProfile: () =>
      apiClient.get('/auth/me'),
      
    updateProfile: (userData: any) =>
      apiClient.put('/auth/profile', userData),
      
    logout: () =>
      apiClient.post('/auth/logout'),
  },

  // Team endpoints
  teams: {
    getAll: () =>
      apiClient.get('/teams'),
      
    getById: (id: string) =>
      apiClient.get(`/teams/${id}`),
      
    create: (teamData: any) =>
      apiClient.post('/teams', teamData),
      
    update: (id: string, teamData: any) =>
      apiClient.put(`/teams/${id}`, teamData),
      
    delete: (id: string) =>
      apiClient.delete(`/teams/${id}`),
      
    assignOwner: (id: string, userId: string) =>
      apiClient.post(`/teams/${id}/assign-owner`, { userId }),

    reorder: (teamIds: string[]) =>
      apiClient.post('/teams/reorder', { teamIds }),
  },

  // Player endpoints
  players: {
    search: (params: any) =>
      apiClient.get('/players/search', { params }),
      
    getById: (id: string) =>
      apiClient.get(`/players/${id}`),

    getNews: (id: string, params?: { limit?: number }) =>
      apiClient.get(`/players/news/${id}`, { params }),
      
    getBySportRadarId: (sportRadarId: string) =>
      apiClient.get(`/players/sportradar/${sportRadarId}`),
      
    getByPosition: (position: string, params?: any) =>
      apiClient.get(`/players/position/${position}`, { params }),
      
    getByTeam: (team: string, params?: any) =>
      apiClient.get(`/players/team/${team}`, { params }),
      
    getStatsSummary: () =>
      apiClient.get('/players/stats/summary'),
      
    updateProjections: (id: string, data: any) =>
      apiClient.put(`/players/${id}/projections`, data),
      
    syncFromSportsDataIO: (season?: string) =>
      apiClient.post('/players/sync', season ? { season } : {}, { timeout: 120000 }),

    syncFromSportradar: () =>
      apiClient.post('/players/sync', {}, { timeout: 120000 }),
      
    assignToTeam: (data: { playerId: string; teamId: string; cost: number; draftId?: string }) =>
      apiClient.post('/players/assign', data),
  },

  // Draft endpoints
  drafts: {
    getAll: () =>
      apiClient.get('/drafts'),
      
    getById: (id: string) =>
      apiClient.get(`/drafts/${id}`),
      
    create: (draftData: any) =>
      apiClient.post('/drafts', draftData),
      
    update: (id: string, draftData: any) =>
      apiClient.put(`/drafts/${id}`, draftData),
      
    delete: (id: string) =>
      apiClient.delete(`/drafts/${id}`),
      
    addTeams: (id: string, teamIds: string[]) =>
      apiClient.post(`/drafts/${id}/teams`, { teamIds }),
      
    start: (id: string) =>
      apiClient.post(`/drafts/${id}/start`),
      
    makePick: (id: string, pickData: any) =>
      apiClient.post(`/drafts/${id}/pick`, pickData),
      
    undoPick: (id: string, pickNumber: number) =>
      apiClient.delete(`/drafts/${id}/picks/${pickNumber}`),
      
    getStatus: (id: string) =>
      apiClient.get(`/drafts/${id}/status`),
      
    // New pick management endpoints
    getPicks: (id: string) =>
      apiClient.get(`/drafts/${id}/picks`),
      
    updatePick: (draftId: string, pickNumber: number, pickData: any) =>
      apiClient.put(`/drafts/${draftId}/picks/${pickNumber}`, pickData),
      
    getRecentPicks: (params?: { limit?: number; draftId?: string }) =>
      apiClient.get('/drafts/picks/recent', { params }),
  },

  // Auction endpoints
  auctions: {
    getActive: (draftId: string) =>
      apiClient.get(`/auctions/draft/${draftId}`),
      
    nominate: (data: any) =>
      apiClient.post('/auctions/nominate', data),
      
    bid: (auctionId: string, bidAmount: number, teamId?: string) =>
      apiClient.post(`/auctions/${auctionId}/bid`, { bidAmount, teamId }),
      
    complete: (auctionId: string) =>
      apiClient.post(`/auctions/${auctionId}/complete`),
  },

  // Users endpoints
  users: {
    getAll: () =>
      apiClient.get('/users'),
  },

  // Utility endpoints
  health: () =>
    apiClient.get('/health'),
    
  status: () =>
    apiClient.get('/status'),
};

// Helper functions
export const handleApiError = (error: any): string => {
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.response?.data?.errors) {
    return error.response.data.errors.join(', ');
  }
  return error.message || 'An unexpected error occurred';
};

export const isApiError = (error: any): boolean => {
  return error.response && error.response.data;
};

export default api;
